import type { ExecutionResult } from '@apollo/react-common';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook, type Action } from '@atlassian/react-sweet-state';
import type { JwmAssociateField } from '../../../services/associate-field/__generated_apollo__/JwmAssociateField';

export type AssociateFieldProps = {
	input: { projectId: string; fieldId: string; issueTypeIds?: number[] };
	showSuccess?: boolean;
	issueType?: string;
	fieldName: string;
	analyticsAttributes?: AssociateAnalyticsAttributes;
};

type AssociateQueueItem = {
	resolve: (value: ExecutionResult<JwmAssociateField>) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	reject: (error: any) => void;
	fieldProps: AssociateFieldProps;
};

type AssociateAnalyticsAttributes = {
	source?: string;
};

type State = {
	associatingFields: string[];
	associateQueue: AssociateQueueItem[];
	isAssociating: boolean;
};

const actions = {
	addAssociatingField:
		(fieldId: string): Action<State> =>
		({ setState, getState }) => {
			setState({ associatingFields: [...getState().associatingFields, fieldId] });
		},
	removeAssociatingField:
		(fieldId: string): Action<State> =>
		({ setState, getState }) => {
			setState({
				associatingFields: getState().associatingFields.filter(
					(assocFieldId) => fieldId !== assocFieldId,
				),
			});
		},
	setAssociateQueue:
		(associateQueue: AssociateQueueItem[]): Action<State> =>
		({ setState }) => {
			setState({ associateQueue });
		},
	addToAssociateQueue:
		(itemToAssociate: AssociateQueueItem): Action<State> =>
		({ setState, getState }) => {
			setState({ associateQueue: [...getState().associateQueue, itemToAssociate] });
		},
	setIsAssociating:
		(isAssociating: boolean): Action<State> =>
		({ setState }) => {
			setState({ isAssociating });
		},
	associate:
		(associateFunction: () => Promise<void>): Action<State> =>
		async ({ setState, getState }) => {
			if (!getState().isAssociating) {
				setState({ isAssociating: true });
				try {
					await associateFunction();
				} finally {
					setState({ isAssociating: false });
				}
			}
		},
};

type Actions = typeof actions;

const Store = createStore<State, Actions>({
	initialState: {
		associatingFields: [],
		associateQueue: [],
		isAssociating: false,
	},
	actions,
	name: 'JwmFieldConfigAssociatingFields',
});

export const useAssociateFieldStore = createHook<State, Actions>(Store);
